import React, { createContext, useEffect, useState } from 'react'
import { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase/client'
import { Profile } from '../lib/types/database.types'

export type ThemePreference = 'light' | 'dark'

interface AuthContextType {
  session: Session | null
  user: User | null
  profile: Profile | null
  loading: boolean
  error: Error | null
  themePreference: ThemePreference
  signInWithMicrosoft: () => Promise<void>
  signOut: () => Promise<void>
  updateThemePreference: (theme: ThemePreference) => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

const normalizeThemePreference = (value: string | null | undefined): ThemePreference =>
  value === 'dark' ? 'dark' : DEFAULT_THEME_PREFERENCE

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [themePreference, setThemePreference] = useState<ThemePreference>(DEFAULT_THEME_PREFERENCE)

  const buildProfilePayload = (authUser: User) => ({
    id: authUser.id,
    email: authUser.email ?? null,
    full_name:
      (typeof authUser.user_metadata?.full_name === 'string' && authUser.user_metadata.full_name) ||
      (typeof authUser.user_metadata?.name === 'string' && authUser.user_metadata.name) ||
      authUser.email ||
      null,
    avatar_url:
      (typeof authUser.user_metadata?.avatar_url === 'string' && authUser.user_metadata.avatar_url) ||
      null,
    role: 'sales',
    theme_preference: DEFAULT_THEME_PREFERENCE,
    quote_first: false,
    is_tester: false,
    is_2nd_tester: false,
    is_coder: false,
    is_task_approver: false,
    is_finance_approver: false,
  })

  const fetchOrCreateProfile = async (authUser: User) => {
    const startTime = performance.now()
    console.log('[Auth] Starting profile fetch for user:', authUser.id)

    try {
      console.log('[Auth] Querying profiles table...')
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle()

      if (fetchError) {
        console.error('[Auth] Profile fetch error:', fetchError)
        // Don't throw - continue with auth even if profile fetch fails
        return null
      }

      if (data) {
        console.log('[Auth] Profile found, setting:', data.id)
        setProfile(data)
        setThemePreference(normalizeThemePreference(data.theme_preference))
        setError(null)
        const elapsed = performance.now() - startTime
        console.log(`[Auth] Profile fetch succeeded in ${elapsed.toFixed(0)}ms`)
        return data
      }

      console.log('[Auth] No profile found, attempting upsert...')
      const { data: insertedProfile, error: insertError } = await supabase
        .from('profiles')
        .upsert(buildProfilePayload(authUser), { onConflict: 'id' })
        .select('*')
        .single()

      if (insertError) {
        console.error('[Auth] Profile insert error:', insertError)
        return null
      }

      console.log('[Auth] Profile created successfully')
      setProfile(insertedProfile)
    setThemePreference(normalizeThemePreference(insertedProfile.theme_preference))
      setError(null)
      const elapsed = performance.now() - startTime
      console.log(`[Auth] Profile creation succeeded in ${elapsed.toFixed(0)}ms`)
      return insertedProfile
    } catch (err) {
      console.warn('[Auth] Unexpected error in profile fetch:', err)
      return null
    }
  }

  useEffect(() => {
    const initAuth = async () => {
      try {
        console.log('[Auth] Recovering session from storage...')
        const {
          data: { session: recoveredSession },
        } = await supabase.auth.getSession()

        console.log('[Auth] Session state:', recoveredSession ? 'valid' : 'none')
        setSession(recoveredSession)
        setUser(recoveredSession?.user ?? null)
        setError(null)

        if (!recoveredSession?.user) {
          setProfile(null)
          setThemePreference(DEFAULT_THEME_PREFERENCE)
        }
      } catch (err) {
        console.error('[Auth] Session recovery error:', err)
        setSession(null)
        setUser(null)
        setProfile(null)
        setThemePreference(DEFAULT_THEME_PREFERENCE)
        setError(null)
      } finally {
        setLoading(false)
      }
    }

    initAuth()

    const { data } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log('[Auth] Auth state change event:', event)
      setSession(newSession)
      setUser(newSession?.user ?? null)
      setError(null)

      if (!newSession?.user) {
        setProfile(null)
        setThemePreference(DEFAULT_THEME_PREFERENCE)
      }

      setLoading(false)
    })

    return () => {
      data.subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setProfile(null)
      setThemePreference(DEFAULT_THEME_PREFERENCE)
      return
    }

    let isCancelled = false

    const syncProfile = async () => {
      console.log('[Auth] Fetching user profile in background...')

      const nextProfile = await fetchOrCreateProfile(user)

      if (isCancelled || nextProfile) {
        return
      }

      console.warn('[Auth] Profile fetch did not return a profile; continuing without one.')
    }

    void syncProfile()

    return () => {
      isCancelled = true
    }
  }, [user])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    document.documentElement.classList.toggle('dark', themePreference === 'dark')
    document.documentElement.style.colorScheme = themePreference
  }, [themePreference])

  const updateThemePreference = async (nextThemePreference: ThemePreference) => {
    if (!user) {
      throw new Error('You must be signed in to update your theme preference')
    }

    const previousThemePreference = themePreference
    setThemePreference(nextThemePreference)

    try {
      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({ theme_preference: nextThemePreference })
        .eq('id', user.id)
        .select('*')
        .single()

      if (updateError) {
        throw updateError
      }

      setProfile(data)
    } catch (err) {
      setThemePreference(previousThemePreference)
      const error = err instanceof Error ? err : new Error('Failed to update theme preference')
      setError(error)
      throw error
    }
  }

  const signInWithMicrosoft = async () => {
    try {
      setLoading(true)
      setError(null)
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      if (err) throw err
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sign in')
      setError(error)
      setLoading(false)
      throw error
    }
  }

  const signOut = async () => {
    try {
      setError(null)

      const { error: err } = await supabase.auth.signOut()
      if (err) throw err

      setSession(null)
      setUser(null)
      setProfile(null)
      setThemePreference(DEFAULT_THEME_PREFERENCE)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to sign out')
      setError(error)
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        loading,
        error,
        themePreference,
        signInWithMicrosoft,
        signOut,
        updateThemePreference,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
